import { Title } from '@zorro/design-system/v2/Title';
import { STEPPER_BODY_MAX_WIDTH } from '@zorro/types';
import { Flex, IPaperProps, Stack, Text } from '@zorro/web/design-system';
import { useAppRouter, useLoadingOverlay } from '@zorro/web/ui-utils';
import { ReactNode } from 'react';

import { FormFooter } from '../../FormFooter';
import { AddEmployerBreadcrumbs } from './AddEmployerBreadcrumbs';
import { AddEmployerStepper } from './AddEmployerStepper';
import {
  AddEmployerStepType,
  HandleStepSubmit,
  getAddEmployerStepConfig,
} from './AddEmployerUtils';

type Props = {
  step: AddEmployerStepType;
  title: string;
  subtitle?: string;
  children: ReactNode;
  onSubmit: HandleStepSubmit;
  isFinalizationMode: boolean;
  isSignedAgreementVisible?: boolean;
  isDon: boolean;
  submitLabel?: string | null;
  isSubmitDisabled?: boolean;
  employerId?: string;
  secondaryLabel?: string;
  onSecondarySubmit?: HandleStepSubmit;
  paperProps?: Omit<IPaperProps, 'children'>;
  rightSection?: ReactNode;
  hasNextPaymentsStep?: boolean;
};

export const AddEmployerStep = ({
  step,
  title,
  subtitle,
  children,
  onSubmit,
  isFinalizationMode,
  isSignedAgreementVisible = false,
  isDon,
  isSubmitDisabled,
  submitLabel = 'Save and continue',
  employerId,
  secondaryLabel,
  onSecondarySubmit,
  paperProps,
  rightSection,
  hasNextPaymentsStep = false,
}: Props) => {
  const { navigateToRoute } = useAppRouter();

  const { startLoading, stopLoading } = useLoadingOverlay();
  const { nextRoute } = getAddEmployerStepConfig(
    step,
    isDon,
    hasNextPaymentsStep
  );

  const handleSubmit = async () => {
    startLoading();
    const result = await onSubmit();
    if (result.navigate && nextRoute) {
      navigateToRoute(nextRoute, [result.employerId]);
    }
    stopLoading();
  };

  const handleSkip = async () => {
    startLoading();
    try {
      if (onSecondarySubmit) {
        const result = await onSecondarySubmit();
        if (result.navigate && nextRoute) {
          navigateToRoute(nextRoute, [result.employerId]);
        }
      } else if (employerId && nextRoute) {
        navigateToRoute(nextRoute, [employerId]);
      }
    } finally {
      stopLoading();
    }
  };

  return (
    <>
      {!isDon && <AddEmployerBreadcrumbs />}
      <AddEmployerStepper
        isDon={isDon}
        employerStep={step}
        employerId={employerId}
        isFinalizationMode={isFinalizationMode}
        isSignedAgreementVisible={isSignedAgreementVisible}
      />
      <Flex mt="xl" pb={100} columnGap={75}>
        <Stack maw={STEPPER_BODY_MAX_WIDTH} gap="xl">
          <Title size="sm">
            {title}
            {subtitle && (
              <Text component="span" c="zorroGray.4" ml="xs">
                ({subtitle})
              </Text>
            )}
          </Title>
          {children}
        </Stack>
        {rightSection && <Stack>{rightSection}</Stack>}
      </Flex>

      <FormFooter
        primaryLabel={submitLabel ?? undefined}
        primaryButtonProps={{
          onClick: handleSubmit,
          disabled: isSubmitDisabled,
        }}
        secondaryLabel={
          employerId && secondaryLabel ? secondaryLabel : undefined
        }
        secondaryButtonProps={{ onClick: handleSkip }}
        paperProps={paperProps}
      />
    </>
  );
};
